/**
 * The loop, drawn.
 *
 * The stack answers "what turns up when things get worse". This answers "what
 * is about to happen", which is a different question on a different axis, so it
 * is a different object. Left to right is time through one loop rather than
 * pressure.
 *
 * Each layer gets a lane showing the peak level of its actual rendered audio,
 * so what you see is the thing you are hearing rather than a guess at it. A
 * lane fades with its own mix level, which means the picture and the pressure
 * control stay in agreement without either knowing about the other.
 *
 * The waveform is drawn once into an offscreen canvas and blitted each frame.
 * Redrawing several thousand peaks at 60fps to move one vertical line would be
 * a lot of work to animate two pixels.
 */

export interface Lane {
  role: string;
  colour: string;
  /** Peak magnitude per column, already reduced to the drawing width. */
  peaks: Float32Array;
}

/** Shared with the label column so the two line up without measuring. */
export const LANE_HEIGHT = 38;
export const LANE_GAP = 6;

export class Timeline {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private cache: HTMLCanvasElement | null = null;
  private lanes: Lane[] = [];
  private dpr = 1;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("this browser has no 2d canvas");
    this.ctx = ctx;
  }

  get height(): number {
    return this.lanes.length * (LANE_HEIGHT + LANE_GAP);
  }

  /**
   * Reduce each stem to one peak per column.
   *
   * Peak rather than average, because an average of a sparse percussive layer
   * is a flat line: the loudest moment in a window is what a listener notices,
   * and it is what makes a hit visible before it arrives.
   */
  load(stems: { role: string; samples: Float64Array; colour: string }[]): void {
    const width = Math.max(1, Math.floor(this.canvas.clientWidth));
    this.lanes = stems.map(({ role, samples, colour }) => {
      const peaks = new Float32Array(width);
      const per = samples.length / width;
      for (let x = 0; x < width; x++) {
        const from = Math.floor(x * per);
        const to = Math.min(samples.length, Math.floor((x + 1) * per));
        let peak = 0;
        for (let i = from; i < to; i++) {
          const v = samples[i] < 0 ? -samples[i] : samples[i];
          if (v > peak) peak = v;
        }
        peaks[x] = peak;
      }
      return { role, colour, peaks };
    });

    this.resize();
    this.cache = null;
  }

  /** Match the backing store to the box and the display density. */
  resize(): void {
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    const width = Math.max(1, Math.floor(this.canvas.clientWidth));
    this.canvas.style.height = `${this.height}px`;
    this.canvas.width = Math.floor(width * this.dpr);
    this.canvas.height = Math.floor(this.height * this.dpr);
    this.cache = null;
  }

  /**
   * @param position seconds into the loop
   * @param duration loop length in seconds
   * @param levels current mix level per role, 0..1
   * @param barSeconds length of one bar, used to shade the bar being played
   */
  draw(
    position: number,
    duration: number,
    levels: Map<string, number>,
    barSeconds = 0,
  ): void {
    if (!this.lanes.length) return;
    if (!this.cache) this.buildCache();

    const w = this.canvas.width;
    const h = this.canvas.height;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, w, h);

    // The bar under the playhead is shaded, because the beat grid below this
    // shows exactly that bar. Without the shading the two are separate
    // pictures and nobody can tell they are the same thing at two zooms.
    if (barSeconds > 0 && duration > 0) {
      const barStart = Math.floor(position / barSeconds) * barSeconds;
      const from = Math.round((barStart / duration) * w);
      const width = Math.max(2, Math.round((barSeconds / duration) * w));
      this.ctx.fillStyle = "rgba(255, 210, 74, 0.13)";
      this.ctx.fillRect(from, 0, width, h);
    }

    // Each lane is blitted at its own opacity, which is why the cache holds
    // them separately rather than as one flattened picture.
    this.lanes.forEach((lane, i) => {
      const top = i * (LANE_HEIGHT + LANE_GAP) * this.dpr;
      const laneH = LANE_HEIGHT * this.dpr;
      this.ctx.globalAlpha = 0.16 + (levels.get(lane.role) ?? 0) * 0.84;
      this.ctx.drawImage(this.cache!, 0, top, w, laneH, 0, top, w, laneH);
    });
    this.ctx.globalAlpha = 1;

    if (duration <= 0) return;
    const x = Math.round((position / duration) * w);
    this.ctx.fillStyle = "#F4F0E2";
    this.ctx.fillRect(x, 0, Math.max(1, Math.round(this.dpr)), h);
  }

  private buildCache(): void {
    const cache = document.createElement("canvas");
    cache.width = this.canvas.width;
    cache.height = this.canvas.height;
    const ctx = cache.getContext("2d");
    if (!ctx) return;

    const w = cache.width;
    this.lanes.forEach((lane, i) => {
      const top = i * (LANE_HEIGHT + LANE_GAP) * this.dpr;
      const laneH = LANE_HEIGHT * this.dpr;
      const mid = top + laneH / 2;

      ctx.fillStyle = lane.colour;
      // A floor of one device pixel, so a silent stretch still reads as a lane
      // rather than as a missing layer.
      const floor = Math.max(1, this.dpr);
      for (let x = 0; x < w; x++) {
        const peak = lane.peaks[Math.floor((x / w) * lane.peaks.length)] ?? 0;
        const height = Math.max(floor, peak * laneH * 0.9);
        ctx.fillRect(x, mid - height / 2, 1, height);
      }
    });

    this.cache = cache;
  }
}
